import { useEnvironment } from "@core/presentation/hook/use-environment/use-environment.hook.ts";
import type { ReactNode } from "react";
import { useEffect, useMemo } from "react";
import { BrandingContext } from "./branding-context.ts";
import { defaultBranding } from "./default.ts";
import brandingConfig from "./index-config.ts";

const developEnv = "localhost";

const updateFavicon = (faviconUrl?: string): void => {
    if (!faviconUrl) return;

    const link = document.querySelector("link[rel~='icon']");

    if (link instanceof HTMLLinkElement) {
        link.href = faviconUrl;
    } else {
        const newLink = document.createElement("link");
        newLink.rel = "icon";
        newLink.type = "image/png";
        newLink.href = faviconUrl;
        document.head.appendChild(newLink);
    }
};

const updateTitle = (title?: string): void => {
    if (!title) return;

    let titleElement = document.querySelector("title");

    if (titleElement instanceof HTMLLinkElement) {
        titleElement.textContent = title;
    } else {
        titleElement = document.createElement("title");
        document.head.appendChild(titleElement);
    }
};

export const BrandingProvider = ({
    children,
}: {
    children: ReactNode;
}): ReactNode => {
    const env = useEnvironment();

    const hostname = useMemo(
        () =>
            window.location.hostname === developEnv
                ? env.mockUrl
                : window.location.hostname,
        [env.mockUrl],
    );

    const branding = useMemo(
        () => brandingConfig[hostname] ?? defaultBranding(),
        [hostname],
    );

    useEffect(() => {
        // Apply CSS Variables
        Object.entries(branding.cssVariables).forEach(([key, value]) => {
            document.documentElement.style.setProperty(key, value);
        });

        // Apply envVariables in globalThis for frontend access
        window.__BRANDING_ENV__ = {
            ...branding.envVariables,
        };

        updateFavicon(branding.envVariables.VITE_IOT_FAVICON);
        updateTitle(branding.envVariables.VITE_IOT_APP_TITLE);
    }, [branding]);

    return (
        <BrandingContext.Provider value={branding}>
            {children}
        </BrandingContext.Provider>
    );
};
